import { CourseData } from "@vixi/types";
import { isCallback, logger } from "@vixi/utils";
import _ from "lodash";
import { createContext, useCallback, useContext, useState } from "react";

interface CourseEditorState {
  /**
   * courseData - The course data being edited
   * @type {CourseData.Course | null}
   */
  courseData?: CourseData.Course | null;
  activeTabs: number[];
  mode: CourseEditorMode;
}

interface SetValueOptions<ValueType, ParentType> {
  condition?: (prev?: ValueType, parent?: ParentType) => boolean;
  mustExist?: boolean;
}

interface CourseEditorActions {
  setCourseData: React.Dispatch<React.SetStateAction<CourseData.Course | null | undefined>>;

  /**
   * setValue - Set a value in the course data at the specified path
   * @param itemPath - The path to the item to set
   * @param value - The value to set at the path, or a function that returns the new value
   * @param options - Options for setting the value
   */
  setValue: <ValueType, ParentType>(
    valuePath: (string | number)[],
    value: ValueType | ((prev: ValueType) => ValueType),
    options?: SetValueOptions<ValueType, ParentType>,
  ) => void;

  /**
   * moveBy - Move an item in an array by n positions
   * @param itemPath - The path to the item to move
   * @param n - The number of positions to move the item by
   * @returns The new index of the item
   */
  moveBy: (itemPath: (string | number)[], n: number) => number;

  /**
   * deleteItem - delete an item from the course data
   * @param itemPath - The path to the item to delete
   */
  deleteItem: (itemPath: (string | number)[]) => void;

  /**
   * duplicateItem - Duplicate an item in an array in the course data
   * @param itemPath - The path to the item to duplicate
   */
  duplicateItem: (itemPath: (string | number)[]) => void;

  /**
   * addItem - Add an item to an array in the course data
   * @param parentPath - The path to the parent array
   * @param item - The item to add to the array
   */
  addItem: (itemPath: (string | number)[], item?: unknown) => void;

  /**
   * setActiveTabs - Set the active tabs in the course editor
   * @param activeTabs - The new active tabs
   */
  setActiveTabs: React.Dispatch<React.SetStateAction<number[]>>;
}

const CourseEditorContext = createContext<(CourseEditorState & CourseEditorActions) | null>(null);

type CourseEditorMode = "edit" | "view";

interface CourseEditorContextProviderProps {
  children?: React.ReactNode;
  courseData?: CourseData.Course | null;
  mode?: CourseEditorMode;
  setCourseData: React.Dispatch<React.SetStateAction<CourseData.Course | null | undefined>>;
}

const CourseEditorContextProvider = (props: CourseEditorContextProviderProps) => {
  const { children, courseData, setCourseData, mode } = props;
  const [activeTabs, setActiveTabs] = useState([1, 1, 1]);

  /**
   * setValue - Set a value in the course data at the specified path
   * @param itemPath - The path to the item to set
   * @param value - The value to set at the path, or a function that returns the new value
   * @param options - Options for setting the value
   */
  const setValue = useCallback(
    <ValueType, ParentType>(
      itemPath: (string | number)[],
      value: ValueType | ((prev: ValueType) => ValueType),
      { condition, mustExist = true }: SetValueOptions<ValueType, ParentType> = {},
    ) => {
      setCourseData((courseData) => {
        if (!courseData) {
          logger.error("Course data not set");
          return courseData;
        }

        // Get the parent object and assert its type
        const parentPath = itemPath.length ? itemPath.slice(0, -1) : null;
        const parent = parentPath
          ? parentPath.length
            ? _.get(courseData, parentPath)
            : courseData
          : null;
        if (parent === undefined) {
          logger.warn(`Invalid path (Couldn't find parent): ${itemPath!.join(".")}`);
          return courseData;
        }

        // Get the current value at the path
        const lastIndex = _.last(itemPath);
        const currentValue = lastIndex ? parent[lastIndex] : parent;

        // Check the condition if provided
        if (
          condition &&
          !condition(currentValue as ValueType | undefined, parent as ParentType | undefined)
        ) {
          return courseData;
        }

        if (mustExist && currentValue === undefined) {
          throw new Error(`Invalid path: ${itemPath.join(".")}`);
        }

        // Set the new value at the path
        if (isCallback(value)) _.set(courseData, itemPath, value(currentValue));
        else _.set(courseData, itemPath, value);

        const res = { ...courseData };

        return res;
      });
    },
    [setCourseData],
  );

  /**
   * moveBy - Move an item in an array by n positions
   * @param itemPath - The path to the item to move
   * @param n - The number of positions to move the item by
   * @returns The new index of the item
   */
  const moveBy = useCallback(
    (itemPath: (string | number)[], n: number) => {
      // Get the parent array and assert its type
      const parentPath = itemPath.slice(0, -1);
      const parent = parentPath.length ? _.get(courseData, parentPath) : courseData;
      if (!parent) throw new Error(`Invalid path: ${parentPath.join(".")}`);
      if (!Array.isArray(parent)) throw new Error(`Invalid path: ${parentPath.join(".")}`);

      // Get the index of the item to move
      const index = itemPath[itemPath.length - 1];
      if (typeof index !== "number") throw new Error(`Invalid path: ${itemPath.join(".")}`);

      // Calculate the new index
      const newIndex = index + n;
      if (newIndex < 0) return 0;
      if (newIndex >= parent.length) return parent.length - 1;

      // Move the item
      const item = parent.splice(index, 1)[0];
      parent.splice(newIndex, 0, item);

      _.set(courseData!, parentPath, [...parent]);

      setCourseData((courseData) => {
        if (!courseData) throw new Error("Course data not set");

        return {
          ...courseData,
        };
      });

      return newIndex;
    },
    [courseData],
  );

  /**
   * deleteItem - delete an item from the course data
   * @param itemPath - The path to the item to delete
   */
  const deleteItem = useCallback(
    (itemPath: (string | number)[]) => {
      const parentPath = itemPath.slice(0, -1);

      // Get the parent array and assert type
      const parent = _.get(courseData, parentPath);
      if (!parent) {
        if (import.meta.env.DEV) debugger;
        throw new Error(`Invalid path: ${parentPath.join(".")}`);
      }
      if (!Array.isArray(parent)) throw new Error(`Invalid path: ${parentPath.join(".")}`);

      // Get the index of the item to delete
      const index = itemPath[itemPath.length - 1];
      if (typeof index !== "number") throw new Error(`Invalid path: ${itemPath.join(".")}`);

      parent.splice(index, 1);

      setValue(parentPath, [...parent]);
    },
    [courseData, setValue],
  );

  /**
   * duplicateItem - Duplicate an item in an array in the course data
   * @param itemPath - The path to the item to duplicate
   */
  const duplicateItem = useCallback(
    (itemPath: (string | number)[]) => {
      const parentPath = itemPath.slice(0, -1);

      // Get the parent array and assert type
      const parent = _.get(courseData, parentPath);
      if (!parent) throw new Error(`Invalid path: ${parentPath.join(".")}`);
      if (!Array.isArray(parent)) throw new Error(`Invalid path: ${parentPath.join(".")}`);

      // Get the index of the item to duplicate
      const index = itemPath[itemPath.length - 1];
      if (typeof index !== "number") throw new Error(`Invalid path: ${itemPath.join(".")}`);

      // Duplicate the item
      const item = _.cloneDeep(parent[index]);
      parent.splice(index, 0, item);

      setValue(parentPath, [...parent]);
    },
    [courseData, setValue],
  );

  /**
   * addItem - Add an item to an array in the course data
   * @param parentPath - The path to the parent array
   * @param item - The item to add to the array
   */
  const addItem = useCallback(
    (parentPath: (string | number)[], item?: unknown) => {
      // Get the parent array and assert type
      const parent = _.get(courseData, parentPath);
      if (!parent) throw new Error(`Invalid path: ${parentPath.join(".")}`);
      if (!Array.isArray(parent)) throw new Error(`Invalid path: ${parentPath.join(".")}`);

      // If no item is provided, duplicate the last item in the array
      if (item === undefined) {
        item = _.cloneDeep(parent[parent.length - 1]);

        if (Object.keys(item as Record<string | number, unknown>).includes("id")) {
          (item as Record<"id", number>).id += 1;
        }
      }

      parent.push(item);
      setValue(parentPath, [...parent]);
    },
    [courseData, setValue],
  );

  return (
    <CourseEditorContext.Provider
      value={{
        courseData,
        setCourseData,
        activeTabs,
        setActiveTabs,
        setValue,
        moveBy,
        deleteItem,
        duplicateItem,
        addItem,
        mode: mode ?? "edit",
      }}
    >
      {children}
    </CourseEditorContext.Provider>
  );
};

const useCourseEditorContext = () => {
  const context = useContext(CourseEditorContext);
  if (!context)
    throw new Error("useCourseEditorContext must be used within a CourseEditorContextProvider");
  return context;
};

export { useCourseEditorContext, CourseEditorContextProvider };
export type { CourseEditorContextProviderProps };
